/**
 * Storage migrations (1.1.0): the words the list, the wizard and the detail page use for statuses, reasons and the
 * window. Pure, so the status lines are unit-tested (apps/web/test/migration-text.test.ts).
 */
import {
  isOpen, nextClosing, nextOpening, wallTimeOf,
  type MigrationDTO, type MigrationFileKind, type MigrationPlanReport, type MigrationItemReason, type MigrationItemStatus, type MigrationPausedReason,
  type MigrationSourceRemoval, type MigrationStatus, type MigrationWarningCode, type TransferWindow,
} from '@prism/contracts';

/** The window (zone and periods) a migration uses, straight from its DTO; null without a window. */
export function windowOfMigration(m: Pick<MigrationDTO, 'window'> | undefined): TransferWindow | null {
  return m?.window ? { timezone: m.window.timezone, periods: m.window.periods } : null;
}

export const MIGRATION_STATUS_LABEL: Record<MigrationStatus, string> = {
  draft: 'Draft',
  planning: 'Planning',
  planned: 'Planned',
  running: 'Running',
  paused: 'Paused',
  copying_done: 'Copied, review',
  removing: 'Removing sources',
  completed: 'Completed',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

/** The pill class for a status (styles.css: .pill.accent, .warning, .danger). */
export function statusTone(s: MigrationStatus): '' | 'accent' | 'warning' | 'danger' | 'ready' {
  if (s === 'running' || s === 'planning' || s === 'removing') return 'accent';
  if (s === 'paused' || s === 'copying_done') return 'warning';
  if (s === 'failed') return 'danger';
  if (s === 'completed') return 'ready';
  return '';
}

export const PAUSED_REASON_LABEL: Record<MigrationPausedReason, string> = {
  manual: 'Paused',
  errors: 'Paused: too many errors',
  destination_full: 'Paused: destination full',
  dest_fill: 'Paused: destination fill limit reached',
  source_offline: 'Paused: source offline, resumes by itself',
  dest_offline: 'Paused: destination offline, resumes by itself',
  dest_unavailable: 'Paused: destination disabled',
};

export const FILE_KIND_LABEL: Record<MigrationFileKind, string> = {
  original: 'Originals',
  derivative: 'Proxies and previews',
  collateral: 'Collateral',
};

/** What the kind rules need to know about a location: a storage agent, or a cold archive. */
type KindEnd = { name: string; kind: string; archive?: { cold?: boolean } | null } | undefined;
const endLabel = (l: NonNullable<KindEnd>) => (l.archive?.cold ? 'an archive' : 'a storage agent');
const special = (l: KindEnd): l is NonNullable<KindEnd> => Boolean(l && (l.kind === 'agent' || l.archive?.cold));

/**
 * The kinds a migration cannot take, each with why, as the API decides them (422 ORIGINALS_ONLY, COLLATERAL_MOVED_ONLY,
 * COLLATERAL_NOT_HERE): an agent or archive destination takes originals only; collateral is moved, never copied, and
 * never to or from an agent or an archive.
 */
export function blockedKinds(action: 'move' | 'copy', source: KindEnd, destination: KindEnd): Partial<Record<MigrationFileKind, string>> {
  const out: Partial<Record<MigrationFileKind, string>> = {};
  if (special(destination)) {
    const why = `“${destination.name}” is ${endLabel(destination)}: only originals can go there.`;
    out.derivative = why;
    out.collateral = why;
  }
  if (!out.collateral) {
    if (action === 'copy') out.collateral = 'Collateral (attachments) is moved, never copied: it is left out of a copy.';
    else if (special(source)) out.collateral = `Collateral is never moved from “${source.name}”, ${endLabel(source)}.`;
  }
  return out;
}

export const REMOVAL_LABEL: Record<MigrationSourceRemoval, string> = {
  after_review: 'After review: every file is copied and verified, then you confirm Remove sources',
  per_file: 'Per file: each source file is removed as soon as its copy is verified',
  after_days: 'After N days: each source file is removed N days after its copy is verified',
};

export const ITEM_STATUS_LABEL: Record<MigrationItemStatus, string> = {
  pending: 'Waiting',
  active: 'In flight',
  verified: 'Verified',
  removed: 'Source removed',
  done: 'Done',
  skipped: 'Skipped',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const ITEM_REASON_LABEL: Record<MigrationItemReason, string> = {
  ORIGINAL_ARCHIVED: 'Only copy is archived',
  ALREADY_PRESENT: 'Already at the destination',
  NOT_PRESENT: 'No longer on the source',
  ASSET_DELETED: 'Asset recycled or purged',
  TRANSFER_ACTIVE: 'Another transfer moves it',
  INTERRUPTED: 'Transfer interrupted',
  TRANSFER_FAILED: 'Transfer failed',
  CHECKSUM_MISMATCH: 'Checksum did not match',
  SOURCE_ALREADY_GONE: 'Source already gone',
  DEST_NOT_VERIFIED: 'Destination copy not verified, source kept',
  CANCELLED: 'Cancelled',
};

export const WARNING_TITLE: Record<MigrationWarningCode, string> = {
  DESTINATION_ROOM: 'Destination room',
  TIERING_RULES: 'Tiering rules',
  OLD_AGENT: 'Storage agent too old to limit bandwidth',
  COLD_SOURCE: 'Cold source',
  UNTHROTTLED_COPY: 'Copies inside one object store',
  NEW_FILES_EXCLUDED: 'New files',
};

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A moment in the window's zone: "Tue 23:00", within the coming 6 days; "Tue 27 Oct 23:00" further out. Without a zone
 * (no window known) the browser's own.
 */
export function zonedWhen(ms: number, tz: string | null, now: number): string {
  const zone = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const w = wallTimeOf(ms, zone);
  const day = WEEKDAY[new Date(Date.UTC(w.y, w.mo - 1, w.d)).getUTCDay()];
  const far = ms - now > 6 * 86_400_000;
  return `${day}${far ? ` ${w.d} ${MONTH[w.mo - 1]}` : ''} ${pad(w.h)}:${pad(w.mi)}`;
}

/** A moment with its date, without seconds: "Wed 21 Oct, 06:00", in the window's zone, else the browser's. */
export function dayDateTime(ms: number, tz: string | null): string {
  const zone = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const w = wallTimeOf(ms, zone);
  const day = WEEKDAY[new Date(Date.UTC(w.y, w.mo - 1, w.d)).getUTCDay()];
  return `${day} ${w.d} ${MONTH[w.mo - 1]}, ${pad(w.h)}:${pad(w.mi)}`;
}

/** The review summary's window: "Weeknights · Mon–Fri 23:00–05:00 (Europe/Helsinki)", or "Any time". */
export function windowSummary(w: { name: string; description: string } | null | undefined): string {
  return w ? `${w.name} · ${w.description}` : 'Any time';
}

/**
 * Under the plan's estimate: "done around Wed 21 Oct, 06:00 if started now", with "; it waits for the window" when the
 * window's closed hours make it end later than the open time alone would (or, with no work time known, when the
 * window was closed as it was planned). Null without a finish time.
 */
export function finishLine(r: Pick<MigrationPlanReport, 'plannedAt' | 'estimate'>, w: TransferWindow | null): string | null {
  if (!r.estimate.finishAt) return null;
  const finish = Date.parse(r.estimate.finishAt);
  const planned = Date.parse(r.plannedAt);
  const waits = Boolean(w) && (r.estimate.workMs != null ? finish - planned > r.estimate.workMs + 60_000 : !isOpen(w!, planned));
  return `done around ${dayDateTime(finish, w?.timezone ?? null)} if started now${waits ? '; it waits for the window' : ''}`;
}

/** What the window is doing now: "Window open, closes Wed 05:00 (Europe/Helsinki)", or null without a window. */
export function windowLine(m: Pick<MigrationDTO, 'window' | 'windowState'>, w: TransferWindow | null, now: number): string | null {
  if (!m.window) return null;
  const tz = w?.timezone ?? null;
  const zone = tz ? ` (${tz})` : '';
  // The window's own hours when they are known (the scheduler's last look can be a tick old), else the scheduler's.
  const open = w ? isOpen(w, now) : m.windowState.open;
  const change = w ? (open ? nextClosing(w, now) : nextOpening(w, now)) : (m.windowState.nextChangeAt ? Date.parse(m.windowState.nextChangeAt) : null);
  if (open) return change != null ? `Window open, closes ${zonedWhen(change, tz, now)}${zone}` : 'Window open';
  return change != null ? `Window closed, opens ${zonedWhen(change, tz, now)}${zone}` : 'Window closed';
}

/**
 * The one-line state the list and the detail page show: "Waiting for the window, opens Tue 23:00 (Europe/Helsinki)",
 * "Daily limit reached", "Paused: destination full", "Planning: 12,400 files so far".
 */
export function statusLine(m: Pick<MigrationDTO, 'status' | 'pausedReason' | 'waitingReason' | 'window' | 'windowState' | 'counters' | 'limits' | 'planError' | 'inFlight'>, w: TransferWindow | null, now: number): string {
  switch (m.status) {
    case 'draft': return 'Draft: plan it to see what moves';
    case 'planning': return m.counters.total.files ? `Planning: ${m.counters.total.files.toLocaleString('en-US')} files so far` : 'Planning';
    case 'planned': return 'Planned, ready to start';
    case 'running': {
      if (m.waitingReason === 'window_closed') {
        const tz = w?.timezone ?? null;
        const at = w ? nextOpening(w, now) : m.windowState.nextChangeAt ? Date.parse(m.windowState.nextChangeAt) : null;
        return at != null && at > now ? `Waiting for the window, opens ${zonedWhen(at, tz, now)}${tz ? ` (${tz})` : ''}` : 'Waiting for the window';
      }
      if (m.waitingReason === 'daily_cap') return 'Daily limit reached, continues tomorrow';
      if (m.inFlight.waitingAdmission > 0 && m.inFlight.running === 0) return 'Running: waiting for a free transfer slot';
      if (m.inFlight.running > 0) return `Running: ${m.inFlight.running} transfer${m.inFlight.running === 1 ? '' : 's'} copying`;
      return 'Running';
    }
    case 'paused': {
      if (m.pausedReason === 'dest_fill' && m.limits.stopAtDestPct != null) return `Paused: destination ${m.limits.stopAtDestPct} % full`;
      return m.pausedReason ? PAUSED_REASON_LABEL[m.pausedReason] : 'Paused';
    }
    case 'copying_done': return 'Copied and verified: review, then remove the sources';
    case 'removing': return 'Removing the source files';
    case 'completed': return m.counters.failed.files ? `Completed, ${m.counters.failed.files.toLocaleString('en-US')} failed` : 'Completed';
    case 'cancelled': return 'Cancelled';
    case 'failed': return m.planError ? `Failed: ${m.planError}` : 'Failed';
  }
}

/** How far ahead an ETA is: "in 3 hours", "in 9 days" (relative times turn into dates after a week). */
export function aheadLabel(iso: string, now: number): string {
  const diff = Date.parse(iso) - now;
  if (diff <= 0) return 'any moment';
  if (diff < 3_600_000) return `in ${Math.max(1, Math.round(diff / 60_000))} min`;
  if (diff < 48 * 3_600_000) return `in ${Math.round(diff / 3_600_000)} hours`;
  return `in ${Math.round(diff / 86_400_000)} days`;
}

/** Finished files and bytes (done, failed or skipped) of the total, 0–100. */
export function progressPct(m: Pick<MigrationDTO, 'counters'>, by: 'files' | 'bytes'): number {
  const c = m.counters;
  const total = c.total[by];
  if (!total) return 0;
  return Math.min(100, ((c.done[by] + c.failed[by] + c.skipped[by]) / total) * 100);
}

/** A move asks for the source location's name before Start and Remove sources; a copy starts without. */
export const startNeedsName = (m: Pick<MigrationDTO, 'action'>) => m.action === 'move';

/** Parses "5, 30, 120" into minutes; null when any part is not a whole number from 1. */
export function parseBackoff(text: string): number[] | null {
  const parts = text.split(/[,\s]+/).filter(Boolean);
  if (!parts.length) return null;
  const n = parts.map(Number);
  return n.every((x) => Number.isInteger(x) && x >= 1) ? n : null;
}

/**
 * How the file list keeps up while the migration runs: by itself while one page is loaded (useMigrationItems refetches
 * every 10 s), by the Refresh button after Load more (a refetch reloads every loaded page).
 */
export function itemsRefresh(running: boolean, pages: number): 'auto' | 'manual' | 'off' {
  if (!running) return 'off';
  return pages > 1 ? 'manual' : 'auto';
}

/**
 * The wizard's live counts: hidden (nothing to count), counting (fetching, or the form changed and the debounce has not
 * caught up), failed, or done. The keys are the preview bodies' JSON ('' for none), compared by value, not identity.
 */
export function previewState(q: { isFetching: boolean; isError: boolean; hasData: boolean }, bodyKey: string, debouncedKey: string): 'hidden' | 'counting' | 'error' | 'ready' {
  if (!bodyKey) return 'hidden';
  if (q.isFetching || bodyKey !== debouncedKey) return 'counting';
  if (q.isError) return 'error';
  return q.hasData ? 'ready' : 'hidden';
}
